import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, LogOut, Shield } from 'lucide-react';
import { useAuthActions, useAuthState } from '../hooks/useAuth';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useMyProjects } from '../hooks/useFirestoreData';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { Input, Textarea, Label } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { getDb } from '../lib/firebase';
import { createProject } from '../lib/firestore';
import {
  emptyProjectsMessage,
  shouldShowCreateProjectButton,
} from '../lib/projectListView';

export function ProjectListPage() {
  const { user } = useAuthState();
  const { logout } = useAuthActions();
  const { isAdmin } = useIsAdmin();
  const { data: projects, loading } = useMyProjects(user?.uid ?? null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onCreate = async () => {
    if (!user) return;
    setBusy(true);
    setErr(null);
    try {
      await createProject(getDb(), {
        name,
        description: desc,
        ownerUserId: user.uid,
        ownerDisplayName: user.displayName ?? user.email ?? user.uid,
        ownerEmail: user.email ?? '',
        ownerPhotoURL: user.photoURL,
      });
      setCreating(false);
      setName('');
      setDesc('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
      <header
        data-testid="project-list-header"
        className="flex items-center justify-between gap-2"
      >
        <div className="flex min-w-0 items-center gap-3">
          <img
            src="/migratus_icon.png"
            alt="MIGRATUS"
            className="h-10 w-10 shrink-0 rounded-lg"
          />
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold tracking-wider sm:text-2xl">
              MIGRATUS
            </h1>
            {/* Sub-line is hidden on phones — the header has to fit the
                action buttons without wrapping. */}
            <p className="hidden truncate text-xs text-slate-500 sm:block">
              {user?.displayName ?? user?.email}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {shouldShowCreateProjectButton(isAdmin) ? (
            <>
              <Button onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4 sm:mr-1" />
                {/* Below `sm` we only show the icon — the full label
                    used to wrap into vertical text on phones. */}
                <span className="hidden sm:inline">新規プロジェクト</span>
              </Button>
              <Link to="/admin">
                <Button variant="secondary" aria-label="管理者設定">
                  <Shield className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">管理者</span>
                </Button>
              </Link>
            </>
          ) : null}
          <Button variant="ghost" onClick={logout} aria-label="サインアウト">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="mt-6">
        {loading ? (
          <Spinner />
        ) : projects.length === 0 ? (
          <div className="card p-8 text-center text-sm text-slate-600">
            {emptyProjectsMessage(isAdmin)}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <Link
                key={p.projectId}
                to={`/projects/${p.projectId}`}
                className="card p-4 transition-shadow hover:shadow-md"
              >
                <div className="font-semibold">{p.name}</div>
                <p className="mt-1 line-clamp-2 text-sm text-slate-600">
                  {p.description || '（説明なし）'}
                </p>
                <div className="mt-3 text-xs text-slate-500">
                  メンバー {p.memberUserIds.length} 名
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Dialog
        open={creating}
        onClose={() => setCreating(false)}
        title="新規プロジェクト"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              キャンセル
            </Button>
            <Button onClick={onCreate} disabled={busy || !name.trim()}>
              作成
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="pname">プロジェクト名</Label>
            <Input
              id="pname"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: 受注管理移行 Phase 1"
            />
          </div>
          <div>
            <Label htmlFor="pdesc">説明</Label>
            <Textarea
              id="pdesc"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>
          {err ? <p className="text-xs text-red-600">{err}</p> : null}
        </div>
      </Dialog>
    </div>
  );
}
